/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useMemo, useRef, useEffect} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {apiAccessRequestAPI} from '../../../api'
import toastr from 'toastr';
import { useIntl } from 'react-intl'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { MenuComponent } from '../../../../_metronic/assets/ts/components'

import {APIAccessRequestForm} from '../../../components/APIAccessRequestForm'
import {EmailVerificationModal} from './EmailVerificationModal'

import { APP_NAME } from '../../../utils';

const initialValues = {
  full_name: '',
  email: '',
  organization: '',
  use_case: '',
  terms_agreed: false,
  secret: '',
  verification_code: ''
}

const requestSchema = Yup.object().shape({
  full_name: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters')
    .required('Required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Required'),
  organization: Yup.string()
    .min(2, 'Minimum 2 characters')
    .max(100, 'Maximum 100 characters')
    .required('Required'),
  use_case: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(500, 'Maximum 500 characters')
    .required('Required'),
  terms_agreed: Yup.boolean().required('Required').oneOf([true], 'You must agree to continue')
});

export function Request() {
  const fullNameInput = useRef<HTMLInputElement | null>(null)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [handling, setHandling] = useState(false)
  const [secret, setSecret] = useState<string>()

  const intl = useIntl()
  const selectedLang = useLang()

  useEffect(() => {
    if (fullNameInput.current) {
      fullNameInput.current.focus()
    }
    MenuComponent.reinitialization()
  }, [])

  
  const formik = useFormik({
    initialValues,
    validationSchema: requestSchema,
    onSubmit: async (values) => {
      sendVerificaitonCode(values.email, false)
    },
  })

  const submit = async (verification_code) => {
    try{
      if( secret && verification_code ){
        const values = formik.values
        values['secret'] = secret
        values['verification_code'] = verification_code
        values['origin'] = APP_NAME
        values['language_code'] = selectedLang
        await apiAccessRequestAPI.add(values)
        toastr.success(intl.formatMessage({id: `api_access_request.submitted`}))
        setShowVerificationModal(false)
        formik.resetForm()
      }
    } catch(error: any) {
      const { status, config, data } = error.response
      if (status === 400){
        const message = data['message'].toLowerCase()
        if( message.includes('invalid')){
          toastr.info(intl.formatMessage({id: `api_access_request.verification.invalid`}))
        }else if (message.includes('expired')){
          toastr.info(intl.formatMessage({id: `api_access_request.verification.expired`}))
        }
      } else if (status === 409){
        toastr.info(intl.formatMessage({id: `api_access_request.already_submitted`}))
        setShowVerificationModal(false)
      } else if (status === 429){
        toastr.error(intl.formatMessage({id: `api_access_request.verification.attempt_limit`}))
      }
    }
  }

  const sendVerificaitonCode = async (email:string, isResending:boolean) => {
    setHandling(true)
    try{
      let {data: data} = await apiAccessRequestAPI.sendVerificationCode(email, selectedLang)
      if( data['message'] == 'backoff' ){
        toastr.info(intl.formatMessage({id: `api_access_request.verification.backoff`}, {seconds: parseInt(data['data']['backoff_in_seconds'])}))
      }else{
        isResending && toastr.info( intl.formatMessage({id: `api_access_request.verification.resent`}))
        setSecret(data['data']['secret'])
        setShowVerificationModal(true)
      }
      setHandling(false)
    } catch(error: any) {
      setHandling(false)
    }
  }

  const resendVerificationCode = () => {
    sendVerificaitonCode(formik.values.email, true)
  }

  return (
    <>
    <form
      className='form w-100 fv-plugins-bootstrap5 mw-350px fv-plugins-framework'
      onSubmit={formik.handleSubmit}
      noValidate
    >
      <div className='text-center mb-10'>
        <h1 className='text-dark display-6 pb-0'>{intl.formatMessage({id: `api_access_request.title`})}</h1>
      </div>
      <APIAccessRequestForm fullNameInput={fullNameInput} formik={formik}/>
      <div className='d-grid'>
        <button
          type='submit'
          className='btn btn-lg btn-primary px-8 py-5 w-100 fs-4'
          disabled={handling || !formik.isValid}
        >
          {!handling && <span className='indicator-label'>{intl.formatMessage({id: `api_access_request.request`})}</span>}
          {handling && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {intl.formatMessage({id: `general.please_wait`})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
    { showVerificationModal && <EmailVerificationModal show={showVerificationModal} handleClose={() => setShowVerificationModal(false)} handleSubmit={submit} handleResend={resendVerificationCode} />}
    </>
  )
}
